import styled, { css } from "styled-components";

interface OutlinedStyleProps {
  $outlined: boolean;
}

const overlaySurface = css<OutlinedStyleProps>`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background-color: ${({ $outlined, theme }) =>
    $outlined ? "transparent" : theme.colors.accent};

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    background-color: ${({ $outlined, theme }) =>
      $outlined ? theme.colors.accent : theme.colors.darkBlue};
    opacity: ${({ $outlined }) => ($outlined ? 0.14 : 0.42)};
    transform: translateY(101%);
    transition: transform 0.35s cubic-bezier(0.22, 1, 0.36, 1);
  }
`;

export const StyledButton = styled.button<{
  $withArrow: boolean;
  $outlined: boolean;
}>`
  ${({ $withArrow }) => !$withArrow && overlaySurface}
  display: inline-flex;
  width: max-content;
  inline-size: max-content;
  max-width: 100%;
  flex: 0 0 auto;
  align-self: flex-start;
  justify-self: start;
  align-items: stretch;
  justify-content: center;
  gap: 0;
  padding: ${({ $withArrow, theme }) =>
    $withArrow ? "0" : theme.padding.button};
  border: ${({ $outlined, $withArrow, theme }) =>
    $withArrow
      ? "0"
      : `2px solid ${$outlined ? theme.colors.accent : "transparent"}`};
  border-radius: 999px;
  background-color: ${({ $withArrow }) =>
    $withArrow ? "transparent" : undefined};
  color: ${({ $outlined, theme }) =>
    $outlined ? theme.colors.accent : theme.colors.offWhite};
  font-size: ${({ theme }) => theme.typography.body.fontSize};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  line-height: auto;
  cursor: pointer;
  transition: box-shadow 3s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover::before,
  &:hover > span::before {
    transform: translateY(0);
  }

  &:focus {
    outline: none;
  }

  &:focus-visible:not(:disabled) {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
  }

  &:active:not(:disabled) {
    box-shadow: ${({ $withArrow, theme }) =>
      $withArrow ? "none" : theme.shadow.buttonActive};
    transition-duration: 0.08s;
  }

  &:active:not(:disabled) > span {
    box-shadow: ${({ $withArrow, theme }) =>
      $withArrow ? theme.shadow.buttonActive : "none"};
    transition-duration: 0.08s;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  @media (max-width: 520px) {
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? "0" : theme.padding.buttonMobile};
    font-size: ${({ theme }) => theme.typography.medium.fontSize};
  }

  @media (prefers-reduced-motion: reduce) {
    &,
    &::before,
    & > span::before {
      transition-duration: 0.01ms;
    }
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? "0" : theme.padding.buttonLarge};
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.5rem;
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? "0" : theme.padding.buttonXLarge};
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
    padding: ${({ $withArrow }) => ($withArrow ? "0" : "30px 82px")};
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
    padding: ${({ $withArrow }) => ($withArrow ? "0" : "32px 82px")};
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
    padding: ${({ $withArrow }) => ($withArrow ? "0" : "40px 100px")};
  }
`;

export const ButtonContent = styled.span<{
  $withArrow: boolean;
  $outlined: boolean;
}>`
  ${({ $withArrow }) => $withArrow && overlaySurface}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${({ $withArrow, theme }) =>
    $withArrow ? theme.padding.button : "0"};
  border: ${({ $outlined, $withArrow, theme }) =>
    $withArrow
      ? `2px solid ${$outlined ? theme.colors.accent : "transparent"}`
      : "0"};
  border-radius: 999px;
  white-space: nowrap;
  transition: box-shadow 3s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 520px) {
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? theme.padding.buttonMobile : "0"};
  }

  @media (min-width: 2500px) {
    font-size: 1.75rem;
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? theme.padding.buttonLarge : "0"};
  }
  @media (min-width: 3000px) {
    font-size: 2rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.5rem;
    padding: ${({ $withArrow, theme }) =>
      $withArrow ? theme.padding.buttonXLarge : "0"};
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
    padding: ${({ $withArrow }) => ($withArrow ? "30px 82px" : "0")};
  }
  @media (min-width: 4500px) {
    font-size: 3.25rem;
    padding: ${({ $withArrow }) => ($withArrow ? "32px 82px" : "0")};
  }
  @media (min-width: 5000px) {
    font-size: 3.75rem;
    padding: ${({ $withArrow }) => ($withArrow ? "40px 100px" : "0")};
  }
`;

export const ArrowIcon = styled.span<OutlinedStyleProps>`
  ${overlaySurface}
  display: inline-flex;
  align-items: center;
  justify-content: center;
  aspect-ratio: 1;
  min-width: 59px;
  border: 2px solid
    ${({ $outlined, theme }) =>
      $outlined ? theme.colors.accent : "transparent"};
  border-radius: 50%;
  font-size: 1.5rem;
  transition: box-shadow 3s cubic-bezier(0.16, 1, 0.3, 1);

  i {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 520px) {
    font-size: ${({ theme }) => theme.typography.body.fontSize};
    min-width: 46px;
  }

  @media (min-width: 2500px) {
    font-size: 2rem;
    min-width: 82px;
  }
  @media (min-width: 3000px) {
    font-size: 2.25rem;
  }
  @media (min-width: 3400px) {
    font-size: 2.5rem;
    min-width: 101px;
  }
  @media (min-width: 3800px) {
    font-size: 3rem;
    min-width: 129px;
  }
  @media (min-width: 4500px) {
    font-size: 3.5rem;
    min-width: 144px;
  }
  @media (min-width: 5000px) {
    font-size: 4rem;
    min-width: 170px;
  }
`;
